import { useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { primary_color } from '../../../../Helper/uiHelper'
import { request } from '../../../../Redux/Sagas/requests/api'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../../Redux/Reducers/notificationHandling'

const formatApiMessage = (message) => {
  if (!message) return 'Failed to assign new code'
  if (typeof message === 'string') return message
  if (typeof message === 'object') {
    return Object.values(message).flat().join(' ')
  }
  return 'Failed to assign new code'
}

/**
 * Purchase / booking: advance Automatic Code sequence (+1) when year started & code issued.
 * Flow: confirm → generate → ask to re-send confirmation.
 */
const NewCodeButton = ({ itemId, disabled, blockedMessage }) => {
  const dispatch = useDispatch()
  const [step, setStep] = useState(null) // null | 'confirm' | 'resend'
  const [loading, setLoading] = useState(false)
  const [newCodeLabel, setNewCodeLabel] = useState(null)

  const closeAll = () => {
    if (loading) return
    setStep(null)
    setNewCodeLabel(null)
  }

  const generateCode = () => {
    setLoading(true)
    request('automatic-codes/new-code', 'POST', { item_id: itemId, resend_email: false }).then((res) => {
      setLoading(false)
      if (res?.status === 200) {
        setNewCodeLabel(res?.data?.data?.code || null)
        dispatch(
          setNotification({
            type: 'success',
            message: res?.data?.message || `New code: ${res?.data?.data?.code}`,
          })
        )
        setStep('resend')
      } else {
        dispatch(setNotification({ type: 'error', message: formatApiMessage(res?.data?.message) }))
        setStep(null)
      }
    })
  }

  const resendConfirmation = (shouldSend) => {
    if (!shouldSend) {
      closeAll()
      return
    }
    setLoading(true)
    request('automatic-codes/new-code', 'POST', { item_id: itemId, resend_email_only: true }).then((res) => {
      setLoading(false)
      if (res?.status === 200) {
        dispatch(
          setNotification({
            type: 'success',
            message: res?.data?.message || 'Confirmation email sent.',
          })
        )
      } else {
        dispatch(setNotification({ type: 'error', message: formatApiMessage(res?.data?.message) }))
      }
      setStep(null)
      setNewCodeLabel(null)
    })
  }

  const openConfirm = () => {
    if (disabled) {
      dispatch(
        setNotification({
          type: 'error',
          message: blockedMessage || 'Cannot change the code until the code issue date has passed, or after the year close date.',
        })
      )
      return
    }
    setNewCodeLabel(null)
    setStep('confirm')
  }

  return (
    <>
      <Button
        size='sm'
        disabled={disabled || loading}
        title={disabled ? blockedMessage || 'Code changes are blocked until issue date, or after year close' : undefined}
        style={{ background: primary_color, border: 'none', opacity: disabled ? 0.55 : 1 }}
        onClick={openConfirm}>
        New Code
      </Button>

      <Modal isOpen={step === 'confirm'} toggle={closeAll} centered style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={closeAll}>New Code</ModalHeader>
        <ModalBody>
          <p className='subtitle1 mb-0'>Changing the code will mean your old code will no longer work. Continue?</p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={loading} onClick={closeAll}>
            No
          </Button>
          <Button color='primary' style={{ background: primary_color, border: 'none' }} disabled={loading} onClick={generateCode}>
            {loading ? 'Working…' : 'Yes'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={step === 'resend'} toggle={closeAll} centered style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={closeAll}>Confirmation email</ModalHeader>
        <ModalBody>
          {newCodeLabel ? <p className='subtitle1 mb-2'>New code: {newCodeLabel}</p> : null}
          <p className='subtitle1 mb-0'>Do you want to re-send the confirmation?</p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={loading} onClick={() => resendConfirmation(false)}>
            No
          </Button>
          <Button
            color='primary'
            style={{ background: primary_color, border: 'none' }}
            disabled={loading}
            onClick={() => resendConfirmation(true)}>
            {loading ? 'Sending…' : 'Yes'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default NewCodeButton
